import * as Sentry from "@sentry/react";
import { Area } from "react-easy-crop";

import { Attachment } from "./types";

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / 1048576).toFixed(1) + " MB";
};

export const convertBlobToBase64 = (blob: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      const error = new Error("An unknown error has occured");
      Sentry.captureException(error, {
        level: "error",
      });
      reject(error);
    };
    reader.readAsDataURL(blob);
  });
};

const createCroppedImage = (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
): Promise<string> => {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = imageSrc;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        resolve(imageSrc);
        return;
      }

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
      );

      resolve(canvas.toDataURL("image/jpeg"));
    };
  });
};

export const handleImageCroppingAndSetAttachments = async (
  attachment: Attachment,
  croppedAreaPixels: Area | null,
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>,
  onError?: (error: unknown) => void,
): Promise<void> => {
  if (!attachment || !croppedAreaPixels) return;

  try {
    const croppedImageUrl = await createCroppedImage(
      attachment.preview,
      croppedAreaPixels,
    );

    setAttachments((prev) =>
      prev.map((att) =>
        att.id === attachment.id
          ? {
              ...att,
              base64: croppedImageUrl,
              preview: croppedImageUrl,
              cropped: true,
            }
          : att,
      ),
    );
  } catch (error) {
    Sentry.captureException(error, {
      level: "error",
      extra: { label: "Error cropping image" },
    });
    if (onError) {
      onError(error);
    }
  }
};
